import { getFirebaseSingleton } from "@lib/firebase";
import { AUTH_LOGIN_PATH } from "@src/appV2/Auth";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { AppLoadingPage } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { store } from "@store/index";
import { FC, useEffect, useRef } from "react";
import { Redirect, Route, RouteComponentProps, matchPath, useLocation } from "react-router-dom";

import { getAppV2Route } from "./constant/appV2Routes";
import { GlobalRouterPath } from "./constant/globalRoute";
import { PrivateRouteProps } from "./model";

/**
 * @deprecated FIXME: This component is poorly written.
 * Refactor to standard declarative components.
 * https://linear.app/clipboardhealth/issue/FEF-156/rework-publicroute-and-privateroute
 */
export const PrivateRoute: FC<PrivateRouteProps> = ({
  isAuthorized,
  isLoggingOut,
  authStateHasLoaded,
  component,
  ...rest
}) => {
  const isDebuggingEnabled = useCbhFlag(CbhFeatureFlag.WORKER_APP_INITIALIZATION_DEBUGGING, {
    defaultValue: false,
  });

  const { appLoadingPageLog } = useCbhFlag(CbhFeatureFlag.TRACK_INDEXEDDB_SPLASH_HANG, {
    defaultValue: {
      indexedDbOpenExperiment: false,
      appLoadingPageLog: {
        enabled: false,
        timeoutInMs: 10000,
      },
    },
  });

  const isLoggingOutRef = useRef(isLoggingOut);

  const restProps = JSON.stringify(rest);

  const location = useLocation();
  const pathProp = rest.path;

  const doesRouteMatch = Array.isArray(pathProp)
    ? pathProp.some((p) => matchPath(location.pathname, { path: p, exact: false }))
    : Boolean(pathProp && matchPath(location.pathname, { path: pathProp as string, exact: false }));

  useEffect(() => {
    if (isDebuggingEnabled) {
      const firebaseUser = getFirebaseSingleton().currentUser;
      const sessionState = store.getState().session;

      logEvent(APP_V2_APP_EVENTS.APP_LOADING_PAGE_PRIVATE_ROUTE_PROPS, {
        isAuthorized,
        isLoggingOut,
        authStateHasLoaded,
        componentName: component?.name,
        restProps,
        hasFirebaseUser: Boolean(firebaseUser),
        firebaseUserEmail: firebaseUser?.email,
        firebaseUserUid: firebaseUser?.uid,
        // Redux session state
        reduxIsAuthorized: sessionState.isAuthorized,
        reduxAuthStateHasLoaded: sessionState.authStateHasLoaded,
        reduxIsLoggingOut: sessionState.isLoggingOut,
        reduxIsVerifying: sessionState.isVerifying,
        reduxIsSignup: sessionState.isSignup,
        reduxUserId: sessionState.userId,
        reduxUserPhone: sessionState.profile?.phone,
        reduxHasEnv: Boolean(sessionState.env),
        reduxShowAlert: sessionState.showAlert,
        reduxActivePopups: Object.keys(sessionState.popups || {}).filter(
          (key) => sessionState.popups?.[key]?.visible
        ),
      });
    }
  }, [isDebuggingEnabled, isAuthorized, isLoggingOut, authStateHasLoaded, component, restProps]);

  useEffect(() => {
    if (isLoggingOut) {
      isLoggingOutRef.current = true;
    }
  }, [isLoggingOut, isAuthorized, isLoggingOutRef]);

  const renderRoute = (props: RouteComponentProps) => {
    const state = {
      from: props.location,
    };

    if (!isAuthorized) {
      // Redirect to login page if user is logging out.
      if (isLoggingOutRef.current) {
        return <Redirect to={getAppV2Route(AUTH_LOGIN_PATH)} />;
      }

      // Otherwise redirect to welcome page with current location in state
      return <Redirect to={{ pathname: GlobalRouterPath.WELCOME, state }} />;
    }
    const Component = component!;
    return <Component {...props} />;
  };

  return (
    <>
      {authStateHasLoaded && <Route {...rest} render={renderRoute} />}

      {doesRouteMatch && (
        <AppLoadingPage
          isLoading={!authStateHasLoaded}
          parentEventName={APP_V2_APP_EVENTS.APP_LOADING_PAGE_PRIVATE_ROUTE_PROPS}
          isDebuggingEnabled={isDebuggingEnabled || appLoadingPageLog.enabled}
          debugTimeoutInMs={appLoadingPageLog.timeoutInMs}
        />
      )}
    </>
  );
};
